import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { Check, Flag, Minus, ArrowDown, CircleDashed, CalendarBlank, X } from "@phosphor-icons/react";

export const DEFAULT_STATUSES = [
  { key: "por_hacer", label: "Por hacer", color: "#6B7280", done: false },
  { key: "en_progreso", label: "En progreso", color: "#FF5A00", done: false },
  { key: "en_revision", label: "En revisión", color: "#D97706", done: false },
  { key: "bloqueada", label: "Bloqueada", color: "#DC2626", done: false },
  { key: "completada", label: "Completada", color: "#16A34A", done: true },
];
export const STATUS = Object.fromEntries(DEFAULT_STATUSES.map((s) => [s.key, { label: s.label, text: s.color, bg: `${s.color}1A`, border: `${s.color}44` }]));
export const STATUS_ORDER = DEFAULT_STATUSES.map((s) => s.key);
const chipStyle = (color) => ({ background: `${color}1A`, color, border: `1px solid ${color}44` });

export const PRIORITY = {
  urgente: { label: "Urgente", color: "#EF4444", Icon: Flag, weight: "fill" },
  alta: { label: "Alta", color: "#F59E0B", Icon: Flag, weight: "regular" },
  normal: { label: "Normal", color: "#6B7280", Icon: Minus, weight: "bold" },
  baja: { label: "Baja", color: "#3B82F6", Icon: ArrowDown, weight: "bold" },
  sin_prioridad: { label: "Sin prioridad", color: "#CBD5E1", Icon: CircleDashed, weight: "bold" },
};
export const PRIORITY_ORDER = ["urgente", "alta", "normal", "baja", "sin_prioridad"];

export const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0] || "").join("").toUpperCase();
const AV_COLORS = ["#FF5A00", "#3B82F6", "#16A34A", "#D97706", "#8B5CF6", "#EC4899", "#0EA5E9", "#14B8A6"];
export const avatarColor = (id = "") => AV_COLORS[[...String(id)].reduce((a, c) => a + c.charCodeAt(0), 0) % AV_COLORS.length];

export function Avatar({ id, name, size = 24 }) {
  return (
    <span title={name} className="inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0" style={{ width: size, height: size, fontSize: size * 0.42, background: avatarColor(id || name) }}>
      {initials(name)}
    </span>
  );
}

export function StatusChip({ value, statuses }) {
  const arr = statuses && statuses.length ? statuses : DEFAULT_STATUSES;
  const s = arr.find((x) => x.key === value) || arr[0] || { label: value, color: "#6B7280" };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold whitespace-nowrap" style={chipStyle(s.color)}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />{s.label}
    </span>
  );
}

export function StatusEditor({ value, statuses, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const arr = statuses && statuses.length ? statuses : DEFAULT_STATUSES;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><button data-testid={testid} className="outline-none hover:opacity-80 transition-opacity duration-150"><StatusChip value={value} statuses={arr} /></button></PopoverTrigger>
      <PopoverContent align="start" className="w-48 p-1">
        {arr.map((s) => (
          <button key={s.key} data-testid={`status-opt-${s.key}`} onClick={() => { onChange(s.key); setOpen(false); }} className="w-full flex items-center justify-between px-2 py-1.5 rounded-sm hover:bg-neutral-100 transition-colors duration-150">
            <StatusChip value={s.key} statuses={arr} />{value === s.key && <Check size={14} weight="bold" className="text-primary" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function PriorityFlag({ value, showLabel }) {
  const p = PRIORITY[value] || PRIORITY.sin_prioridad;
  const Icon = p.Icon;
  return <span className="inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap" style={{ color: p.color }}><Icon size={15} weight={p.weight} />{showLabel && <span className="text-neutral-700">{p.label}</span>}</span>;
}

export function PriorityEditor({ value, onChange, testid, showLabel = true }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><button data-testid={testid} className="outline-none hover:opacity-80 transition-opacity duration-150"><PriorityFlag value={value} showLabel={showLabel} /></button></PopoverTrigger>
      <PopoverContent align="start" className="w-44 p-1">
        {PRIORITY_ORDER.map((k) => (
          <button key={k} data-testid={`priority-opt-${k}`} onClick={() => { onChange(k); setOpen(false); }} className="w-full flex items-center justify-between px-2 py-1.5 rounded-sm hover:bg-neutral-100 transition-colors duration-150">
            <PriorityFlag value={k} showLabel />{value === k && <Check size={14} weight="bold" className="text-primary" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function AssigneeEditor({ value = [], names = [], users = [], onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) => { const set = new Set(value); set.has(id) ? set.delete(id) : set.add(id); onChange([...set]); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid} className="flex items-center -space-x-1.5 hover:opacity-80 transition-opacity duration-150 min-w-[24px] min-h-[24px]">
          {value.length ? value.slice(0, 3).map((id, i) => <Avatar key={`${id}-${i}`} id={id} name={names[i]} />) : <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-neutral-300 text-neutral-400 text-xs">+</span>}
          {value.length > 3 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-neutral-200 text-neutral-600 text-[10px] font-semibold">+{value.length - 3}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1 max-h-64 overflow-y-auto">
        {users.length === 0 && <p className="text-xs text-neutral-400 px-2 py-2">Sin usuarios</p>}
        {users.map((u) => (
          <button key={u.id || u.name} onClick={() => toggle(u.id)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-neutral-100 transition-colors duration-150">
            <Avatar id={u.id} name={u.name} />
            <span className="text-sm flex-1 text-left truncate">{u.name}</span>
            {value.includes(u.id) && <Check size={14} weight="bold" className="text-primary" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

const fmtDate = (d) => { if (!d) return null; const dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("es-MX", { day: "2-digit", month: "short" }); };
const toISO = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;

export function DateEditor({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const overdue = value && new Date(value + "T00:00:00") < new Date(new Date().toDateString());
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs rounded-sm px-1.5 py-0.5 hover:bg-neutral-100 transition-colors duration-150 ${overdue ? "text-red-600 font-semibold" : "text-neutral-600"}`}>
          <CalendarBlank size={14} />{value ? fmtDate(value) : <span className="text-neutral-400">—</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar mode="single" selected={value ? new Date(value + "T00:00:00") : undefined} onSelect={(d) => { onChange(d ? toISO(d) : null); setOpen(false); }} />
        {value && <button onClick={() => { onChange(null); setOpen(false); }} className="w-full text-xs text-red-600 py-2 border-t border-border hover:bg-neutral-50 flex items-center justify-center gap-1"><X size={12} /> Quitar fecha</button>}
      </PopoverContent>
    </Popover>
  );
}

export function ProgressBar({ value = 0, className = "" }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex-1 h-1.5 rounded-full bg-neutral-200 overflow-hidden"><div className="h-full rounded-full transition-all duration-300" style={{ width: `${Math.min(100, value)}%`, background: value >= 100 ? "#16A34A" : "#FF5A00" }} /></div>
      <span className="text-[11px] text-neutral-500 tabular-nums w-8 text-right">{Math.round(value)}%</span>
    </div>
  );
}

export function TagBadges({ tags = [], onRemove }) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded-md bg-neutral-100 text-neutral-600 border border-neutral-200 px-1.5 py-0.5 text-[11px] font-medium">
          {t}{onRemove && <button onClick={() => onRemove(t)} className="hover:text-red-600"><X size={10} weight="bold" /></button>}
        </span>
      ))}
    </span>
  );
}
